import { trackHeapEvent } from "@hoteltradergit/ht-telemetry-ui-lib";
import { PROMO_EXPIRY_HEAP_EVENT } from "../constants/promoExpiry";

// EO-1184 analytics, named exactly as the spec table names them.
//
// actor_type, property and role ride along automatically on every one of these —
// useHeapIdentify registers them as Heap event properties, which attach to
// every event the SDK sends, so no call site passes them.

/**
 * The hotel dismissed the expiry card.
 *
 * The button keeps its data-heap-id as well; autocapture files that under its
 * own name, which is a separate event rather than a duplicate of this one.
 *
 * @param {Object} args
 * @param {number} [args.promoCount] promos the card was covering when dismissed
 */
export const trackExpiringPromoCardDismissed = ({ promoCount } = {}) =>
  trackHeapEvent(PROMO_EXPIRY_HEAP_EVENT.cardDismissed, {
    promo_count: promoCount ?? null,
    page_section: "action_center",
  });

/**
 * "Review & Extend" followed through to the promotions page.
 *
 * Reported on arrival rather than on click, because the same CTA appears in the
 * expiry email and that click never reaches us. `source` says which surface the
 * hotel came from.
 *
 * @param {Object} args
 * @param {Object} args.promo   { promoId }
 * @param {string} args.source  one of PROMO_EXPIRY_SOURCE
 */
export const trackExpiringPromoCtaClicked = ({ promo = {}, source } = {}) =>
  trackHeapEvent(PROMO_EXPIRY_HEAP_EVENT.ctaClicked, {
    promo_id: promo.promoId ?? promo.id ?? null,
    source: source ?? null,
  });

/**
 * A promo's end date moved forward.
 *
 * The save button looks identical whether the hotel pushed the end date out,
 * pulled it in, or changed the discount and left the dates alone — so this is
 * one the app has to report itself.
 *
 * @param {Object} args
 * @param {Object} args.promo          { promoId, promoName }
 * @param {string} args.previousExpiryDate  end date when the drawer opened
 * @param {string} args.nextExpiryDate      end date that was saved
 * @param {string} [args.entryPoint]   how the drawer was opened
 */
export const trackPromoExtended = ({
  promo = {},
  previousExpiryDate,
  nextExpiryDate,
  entryPoint,
} = {}) =>
  trackHeapEvent(PROMO_EXPIRY_HEAP_EVENT.promoExtended, {
    promo_id: promo.promoId ?? promo.id ?? null,
    promo_name: promo.promoName ?? promo.name ?? null,
    previous_expiry_date: previousExpiryDate ?? null,
    new_expiry_date: nextExpiryDate ?? null,
    entry_point: entryPoint ?? null,
  });
